import { DatePipe } from '@angular/common';
import { Component, Input, OnDestroy, OnInit, inject } from '@angular/core';

import {
    Edp,
    Equipment,
    Faim,
    FaimPremium,
    Phone,
    ProductFactory,
    QuoteModel,
    Sensation,
    Subscription,
    TYPE_MARKETING,
} from '@bytel/bytel-sales';
import { CommercialProposal, FUNDING_ENUM, FakeCartService, FundingMethod, FundingService } from '@bytel/checkout';

import { AddressService } from '@services/address.service';
import { ConfigurationService } from '@services/configuration.service';
import { MainCartService } from '@services/main-cart.service';
import { MessageService } from '@services/message.service';
import { PaymentService } from '@services/payment.service';

import { LazyLoadImageModule } from 'ng-lazyload-image';
import { Subject, mergeMap, of } from 'rxjs';
import { catchError, map, takeUntil } from 'rxjs/operators';

import { DataCyDirective } from '../../../../common/directives/data-cy.directive';
import { PriceFinalEquipmentPipe } from '../../../../shared/pipes/price-final-equipment.pipe';
import { PriceComponent } from '../../../../trilogy/price/price.component';
import { FundingInfosPhoneComponent } from '../modals/funding-infos-phone/funding-infos-phone.component';

@Component({
    selector: 'checkout-equipment',
    templateUrl: './equipment.component.html',
    styleUrl: 'equipment.component.scss',
    standalone: true,
    imports: [LazyLoadImageModule, PriceComponent, DataCyDirective, DatePipe],
})
export class EquipmentComponent implements OnInit, OnDestroy {
    @Input() public quoteIndex: number = 0;

    public equipment: Equipment;
    public defaultImage: string;
    public funding: FundingMethod = null;
    public images: string;
    public showEdp: boolean;
    public edp: Edp = null;
    public odrAmount: number = 0;
    public basePrice: number;
    public mainPriceToDisplay: number;
    public rsfAmount: number;
    public rsfPdf: string;
    public isPreOrder: boolean;
    public isFaimType: boolean;
    public isSas: boolean = false;
    public formatedPreorderDate: Date;
    public commercialProposal: CommercialProposal;
    public canShowInitialPrice: boolean = false;
    public hasCoupon: boolean = false;
    private _ngUnsubscribe = new Subject();

    private paymentService: PaymentService = inject(PaymentService);
    private configurationService: ConfigurationService = inject(ConfigurationService);
    private messageService: MessageService = inject(MessageService);
    private addressService: AddressService = inject(AddressService);
    private fakeCartService: FakeCartService = inject(FakeCartService);
    private cartService: MainCartService = inject(MainCartService);
    private fundingService: FundingService = inject(FundingService);

    public ngOnInit(): void {
        this.equipment = this._getQuote().getPrincipalProduct<Equipment>('Equipment');
        this.isPreOrder = this._getQuote().isPreorder();
        const domain: string = this.configurationService.getData()?.global.origin;
        this.defaultImage = `${domain}/${this.equipment.image}`;
        this.images = `${domain}/${this.equipment.imageHD} 200w,
        ${domain}/${this.equipment.image} 400w`;
        const plan: Subscription = this._getQuote().getPrincipalProduct<Subscription>('Subscription');
        this.isFaimType = ProductFactory.Is(plan, FaimPremium) || ProductFactory.Is(plan, Faim);
        this.isSas = ProductFactory.Is(plan, Sensation, true);

        this.edp = this._getQuote().getProductByType('Edp');
        this.odrAmount = this.equipment.data?.odr?.amount ?? 0;
        this.basePrice = this._getBasePrice();
        this.rsfAmount = (this.equipment as Phone).rsfAmount ?? 0;
        this.rsfPdf = (this.equipment as Phone).rsfPdf ?? '';
        this._setCurrentFunding();

        this.cartService.paymentMethodObs.pipe(takeUntil(this._ngUnsubscribe)).subscribe(() => {
            this._setCurrentFunding();
            this.edp = this._getQuote().getProductByType('Edp');
            this.showEdp = this.edp === null && this.funding?.type === FUNDING_ENUM.MULTI_PRODUCT;
            this._setCommercialProposal();
            this._updateMainPriceToDisplay();
        });

        const preorderDate = this.equipment.data.preorder_to_date;

        // check if preorder date exist and more than today
        if (preorderDate && new Date(preorderDate) > new Date()) {
            this._setEstimatedProductShipping();
        }
        this._setCommercialProposal();
        this.hasCoupon = !!this.equipment.promotions.coupon;
        this.canShowInitialPrice = this._canShowInitialPrice();

        this.showEdp = !!this.edp;

        this._updateMainPriceToDisplay();
    }

    public ngOnDestroy(): void {
        this._ngUnsubscribe.next(null);
        this._ngUnsubscribe.complete();
    }

    /**
     *
     *
     * @memberof EquipmentComponent
     */
    public showFundingDetails(): void {
        this.messageService.openModal(
            FundingInfosPhoneComponent,
            {
                title: this.paymentService.getModalPaymentTitle(this.funding.type, this.commercialProposal),
                equipment: this.equipment,
                funding: this.funding,
                commercialProposal: this.commercialProposal,
            },
            'large',
        );
    }

    private _updateMainPriceToDisplay(): void {
        const priceWithCouponPipe = new PriceFinalEquipmentPipe();
        this.mainPriceToDisplay = this.commercialProposal
            ? priceWithCouponPipe.transform(this.equipment, true, true, this.commercialProposal)
            : priceWithCouponPipe.transform(this.equipment, true, true);
    }

    private _setEstimatedProductShipping(): void {
        this.fakeCartService
            .generateCart(this._getQuote())
            .pipe(
                mergeMap((cart) =>
                    this.addressService.getShippingMethods(cart).pipe(
                        map((res) => {
                            const fastestShippingDate: string = res.sort(
                                (a, b) =>
                                    new Date(a?.estimatedDeveliveryDate).getTime() -
                                    new Date(b?.estimatedDeveliveryDate).getTime(),
                            )[0]?.estimatedDeveliveryDate;
                            this.formatedPreorderDate = new Date(fastestShippingDate);
                        }),
                        takeUntil(this._ngUnsubscribe),
                        catchError((error) => {
                            console.log(error);
                            return of(null);
                        }),
                    ),
                ),
            )
            .subscribe();
    }

    private _setCurrentFunding(): void {
        const fundingMethod = this.cartService.cartModel.fundingMethod;
        this.funding = fundingMethod?.commercialProposal[0]?.nbLoan > 0 ? fundingMethod : null;
    }

    private _setCommercialProposal(): void {
        this.commercialProposal =
            this.funding?.type === 'YOUNITED'
                ? this.fundingService.getYounitedSelectedProposal(this.funding)
                : this.fundingService.getCofidisSelectedProposal(this.funding);
    }

    private _getBasePrice(): number {
        let basePrice = this.equipment.prices.base;
        if (this.isSas) {
            const promoAuto = this.equipment.promotions.automatic.filter(
                (promo) => !promo.typesMarketing.includes(TYPE_MARKETING.SOLDES),
            );
            basePrice -= promoAuto.reduce((acc, promo) => acc + promo.reductionAmount, 0);
        }
        return basePrice;
    }

    private _canShowInitialPrice(): boolean {
        if (!this.isSas) {
            return true;
        }

        const hasPromoSales = this.equipment.promotions.automatic.some((promo) =>
            promo.typesMarketing.includes(TYPE_MARKETING.SOLDES),
        );

        return hasPromoSales || this.hasCoupon;
    }

    private _getQuote(): QuoteModel {
        return this.cartService.getQuote(this.quoteIndex);
    }
}
